import {
  modelAssetSchema,
  appearanceStateSchema,
  appearanceTicketSchema,
} from "@stonelab/contracts";
import type {
  ModelAsset,
  AppearanceState,
  SaveAppearance,
  AppearanceTicket,
} from "@stonelab/contracts";
import {
  shippingPolicySchema,
  shippingQuoteSchema,
  adminAccountSchema,
  adminCategoryListResponseSchema,
  adminOrderListResponseSchema,
  adminWorkListResponseSchema,
  adminWorkSchema,
  designerListResponseSchema,
  homeContentSchema,
  orderSchema,
  uploadMediaResponseSchema,
  adminLoginResponseSchema,
  adminUserListResponseSchema,
  adminUserSchema,
  adminStoneListResponseSchema,
  adminStoneSchema,
  apiErrorSchema,
} from "@stonelab/contracts";
import type {
  ShippingPolicy,
  ShippingQuote,
  SaveShippingPolicy,
  ShippingPreviewRequest,
  AdminAccountPayload,
  AdminCategoryPayload,
  AdminOrderListQueryPayload,
  AdminOrderListResponsePayload,
  AdminWorkListQueryPayload,
  AdminWorkListResponsePayload,
  AdminWorkPayload,
  CreateDesignerRequestPayload,
  CreateWorkRequestPayload,
  DesignerListResponsePayload,
  HomeContentPayload,
  OrderPayload,
  UpdateFulfillmentRequestPayload,
  SubmitOrderProofPayload,
  ReviewRefundPayload,
  UpdateDesignerRequestPayload,
  UpdateHomeContentRequestPayload,
  UpdateWorkRequestPayload,
  UploadMediaResponsePayload,
  AdminLoginResponsePayload,
  AdminUserListResponsePayload,
  AdminUserPayload,
  AdminStoneListQueryPayload,
  AdminStoneListResponsePayload,
  AdminStonePayload,
  CreateCategoryRequestPayload,
  CreateSpecRequestPayload,
  CreateStoneRequestPayload,
  ChangePasswordRequestPayload,
  CreateAdminUserRequestPayload,
  UpdateAdminUserRequestPayload,
  UpdateSpecRequestPayload,
  UpdateStoneRequestPayload,
} from "@stonelab/contracts";

/**
 * 后台调用服务端的唯一入口。
 *
 * 三条不可动摇的规则：
 * - **响应过契约校验**：服务端结构与契约不一致时要立刻炸出来，不能默默当成可用数据用下去。
 * - **错误按 code 判断**：界面不匹配中文文案，文案会变。
 * - **会话失效统一处理**：任何一个接口返回 401 都通知上层跳登录，不在各处重复判断。
 *
 * 令牌与跳转都由外部注入：这一层不碰 localStorage，也不碰路由，因此可以在测试里
 * 用假令牌与假响应完整验证。
 */

/** 客户端自己产生的错误码。与服务端错误码区分开，避免混淆来源。 */
export const CLIENT_ERROR_CODES = {
  /** 请求没到服务端（断网、服务没起、被代理拦下）。 */
  NETWORK: "CLIENT_NETWORK_ERROR",
  /** 服务端返回的结构不符合契约。 */
  INVALID_RESPONSE: "CLIENT_INVALID_RESPONSE",
} as const;

/**
 * 只用到契约 Schema 的两个行为：能校验、能给出数据。
 * 这样写不必在后台引入 zod 的类型，也避免把校验库锁进这一层。
 */
interface Schema<T> {
  safeParse(value: unknown): { success: true; data: T } | { success: false; error: unknown };
}

export class AdminApiError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown> | null;
  readonly requestId: string | null;

  constructor(
    code: string,
    message: string,
    options: { details?: Record<string, unknown> | null; requestId?: string | null } = {},
  ) {
    super(message);
    this.name = "AdminApiError";
    this.code = code;
    this.details = options.details ?? null;
    this.requestId = options.requestId ?? null;
  }
}

export interface AdminApiOptions {
  /** 接口基址，例如 `/api/v1`。 */
  baseUrl: string;
  /** 取当前令牌。返回 null 表示未登录。 */
  getToken(): string | null;
  /** 服务端判定会话无效或过期时回调，用于跳转登录页。 */
  onUnauthorized?(error: AdminApiError): void;
}

export interface AdminApi {
  uploadModel(file: File): Promise<ModelAsset>;
  getAppearance(id: string): Promise<AppearanceState>;
  saveAppearance(id: string, input: SaveAppearance): Promise<AppearanceState>;
  previewAppearance(id: string, revision: number): Promise<AppearanceTicket>;
  publishAppearance(id: string, revision: number): Promise<AppearanceState>;
  getShippingPolicy(): Promise<ShippingPolicy>;
  saveShippingPolicy(payload: SaveShippingPolicy): Promise<ShippingPolicy>;
  previewShipping(payload: ShippingPreviewRequest): Promise<ShippingQuote>;
  login(username: string, password: string): Promise<AdminLoginResponsePayload>;
  logout(): Promise<void>;
  me(): Promise<AdminAccountPayload>;
  changePassword(payload: ChangePasswordRequestPayload): Promise<void>;

  listUsers(): Promise<AdminUserListResponsePayload>;
  createUser(payload: CreateAdminUserRequestPayload): Promise<AdminUserPayload>;
  updateUser(id: string, patch: UpdateAdminUserRequestPayload): Promise<AdminUserPayload>;

  listStones(query: AdminStoneListQueryPayload): Promise<AdminStoneListResponsePayload>;
  getStone(id: string): Promise<AdminStonePayload>;
  createStone(payload: CreateStoneRequestPayload): Promise<AdminStonePayload>;
  updateStone(id: string, patch: UpdateStoneRequestPayload): Promise<AdminStonePayload>;
  addSpec(stoneId: string, payload: CreateSpecRequestPayload): Promise<AdminStonePayload>;
  updateSpec(specId: string, patch: UpdateSpecRequestPayload): Promise<AdminStonePayload>;
  uploadPhoto(stoneId: string, file: File): Promise<AdminStonePayload>;

  listCategories(): Promise<AdminCategoryPayload[]>;
  createCategory(payload: CreateCategoryRequestPayload): Promise<AdminCategoryPayload[]>;

  /** 通用素材上传：商品照片、作品主图与实拍图、设计师头像、首页大图都走它。 */
  uploadMedia(file: File): Promise<UploadMediaResponsePayload>;

  listWorks(query: AdminWorkListQueryPayload): Promise<AdminWorkListResponsePayload>;
  getWork(id: string): Promise<AdminWorkPayload>;
  createWork(payload: CreateWorkRequestPayload): Promise<AdminWorkPayload>;
  updateWork(id: string, patch: UpdateWorkRequestPayload): Promise<AdminWorkPayload>;

  listDesigners(): Promise<DesignerListResponsePayload>;
  createDesigner(payload: CreateDesignerRequestPayload): Promise<DesignerListResponsePayload>;
  updateDesigner(
    id: string,
    patch: UpdateDesignerRequestPayload,
  ): Promise<DesignerListResponsePayload>;

  getHomeContent(): Promise<HomeContentPayload>;
  updateHomeContent(payload: UpdateHomeContentRequestPayload): Promise<HomeContentPayload>;

  listOrders(query: AdminOrderListQueryPayload): Promise<AdminOrderListResponsePayload>;
  getOrder(id: string): Promise<OrderPayload>;
  cancelOrder(id: string): Promise<OrderPayload>;
  updateFulfillment(id: string, input: UpdateFulfillmentRequestPayload): Promise<OrderPayload>;
  submitOrderProof(id: string, input: SubmitOrderProofPayload): Promise<OrderPayload>;
  reviewRefund(id: string, input: ReviewRefundPayload): Promise<OrderPayload>;
}

interface ParsedResponse {
  status: number;
  /** 空响应体（204 之类）为 null。 */
  body: unknown;
}

function buildWorkQuery(query: AdminWorkListQueryPayload): string {
  const params = new URLSearchParams();
  if (query.keyword !== undefined) params.set("keyword", query.keyword);
  if (query.kind !== undefined) params.set("kind", query.kind);
  if (query.status !== undefined) params.set("status", query.status);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));

  const text = params.toString();
  return text.length === 0 ? "" : `?${text}`;
}

function buildQuery(query: AdminStoneListQueryPayload): string {
  const params = new URLSearchParams();
  if (query.keyword !== undefined) params.set("keyword", query.keyword);
  if (query.categoryId !== undefined) params.set("categoryId", query.categoryId);
  if (query.status !== undefined) params.set("status", query.status);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));

  const text = params.toString();
  return text.length === 0 ? "" : `?${text}`;
}

function buildOrderQuery(query: AdminOrderListQueryPayload): string {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set("status", query.status);
  if (query.keyword !== undefined) params.set("keyword", query.keyword);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));

  const text = params.toString();
  return text.length === 0 ? "" : `?${text}`;
}

export function createAdminApi(options: AdminApiOptions): AdminApi {
  async function send(
    method: string,
    path: string,
    init: { body?: unknown; form?: FormData } = {},
  ): Promise<ParsedResponse> {
    const headers: Record<string, string> = {};
    const token = options.getToken();
    if (token !== null) {
      headers["authorization"] = `Bearer ${token}`;
    }

    let body: BodyInit | undefined;
    if (init.form !== undefined) {
      body = init.form;
    } else if (init.body !== undefined) {
      headers["content-type"] = "application/json";
      body = JSON.stringify(init.body);
    }

    let response: Response;
    try {
      response = await fetch(`${options.baseUrl}${path}`, {
        method,
        headers,
        ...(body === undefined ? {} : { body }),
      });
    } catch (error) {
      throw new AdminApiError(CLIENT_ERROR_CODES.NETWORK, "连不上服务器，请确认服务端已启动", {
        details: { cause: error instanceof Error ? error.message : String(error) },
      });
    }

    const text = await response.text();
    if (text.length === 0) {
      return { status: response.status, body: null };
    }

    try {
      return { status: response.status, body: JSON.parse(text) as unknown };
    } catch {
      throw new AdminApiError(CLIENT_ERROR_CODES.INVALID_RESPONSE, "服务端返回的内容无法解析");
    }
  }

  function assertOk(response: ParsedResponse): unknown {
    if (response.status >= 200 && response.status < 300) {
      return response.body;
    }

    const parsed = apiErrorSchema.safeParse(response.body);
    const error = parsed.success
      ? new AdminApiError(parsed.data.code, parsed.data.message, {
          details: parsed.data.details ?? null,
          requestId: parsed.data.requestId,
        })
      : new AdminApiError(
          CLIENT_ERROR_CODES.INVALID_RESPONSE,
          `请求失败（HTTP ${String(response.status)}），且返回结构不符合契约`,
        );

    if (response.status === 401) {
      options.onUnauthorized?.(error);
    }
    throw error;
  }

  /** 解析响应并校验契约。结构不符时抛错，而不是把不确定的数据交给界面。 */
  function parseWith<T>(schema: Schema<T>, value: unknown, what: string): T {
    const result = schema.safeParse(value);
    if (!result.success) {
      throw new AdminApiError(
        CLIENT_ERROR_CODES.INVALID_RESPONSE,
        `${what}的结构与服务端契约不一致`,
      );
    }
    return result.data;
  }

  return {
    async uploadModel(file) {
      const form = new FormData();
      form.append("file", file);
      return parseWith(
        modelAssetSchema,
        assertOk(await send("POST", "/admin/models", { form })),
        "模型校验",
      );
    },
    async getAppearance(id) {
      return parseWith(
        appearanceStateSchema,
        assertOk(await send("GET", `/admin/specs/${id}/appearance`)),
        "三维外观",
      );
    },
    async saveAppearance(id, input) {
      return parseWith(
        appearanceStateSchema,
        assertOk(await send("PUT", `/admin/specs/${id}/appearance`, { body: input })),
        "三维草稿",
      );
    },
    async previewAppearance(id, revision) {
      return parseWith(
        appearanceTicketSchema,
        assertOk(
          await send("POST", `/admin/specs/${id}/appearance/preview`, { body: { revision } }),
        ),
        "预览码",
      );
    },
    async publishAppearance(id, revision) {
      return parseWith(
        appearanceStateSchema,
        assertOk(
          await send("POST", `/admin/specs/${id}/appearance/publish`, {
            body: { revision, confirmed: true },
          }),
        ),
        "发布结果",
      );
    },
    async getShippingPolicy() {
      return parseWith(
        shippingPolicySchema,
        assertOk(await send("GET", "/admin/shipping")),
        "运费规则",
      );
    },
    async saveShippingPolicy(payload) {
      return parseWith(
        shippingPolicySchema,
        assertOk(await send("PUT", "/admin/shipping", { body: payload })),
        "运费规则",
      );
    },
    async previewShipping(payload) {
      return parseWith(
        shippingQuoteSchema,
        assertOk(await send("POST", "/admin/shipping/preview", { body: payload })),
        "运费试算",
      );
    },
    async login(username, password) {
      const response = await send("POST", "/admin/auth/login", { body: { username, password } });
      return parseWith(adminLoginResponseSchema, assertOk(response), "登录结果");
    },

    async logout() {
      assertOk(await send("POST", "/admin/auth/logout"));
    },

    async me() {
      const response = await send("GET", "/admin/auth/me");
      return parseWith(adminAccountSchema, assertOk(response), "账号信息");
    },

    async changePassword(payload) {
      assertOk(await send("POST", "/admin/auth/password", { body: payload }));
    },

    async listUsers() {
      const response = await send("GET", "/admin/users");
      return parseWith(adminUserListResponseSchema, assertOk(response), "账号列表");
    },

    async createUser(payload) {
      const response = await send("POST", "/admin/users", { body: payload });
      return parseWith(adminUserSchema, assertOk(response), "新建账号结果");
    },

    async updateUser(id, patch) {
      const response = await send("PATCH", `/admin/users/${id}`, { body: patch });
      return parseWith(adminUserSchema, assertOk(response), "账号保存结果");
    },

    async listStones(query) {
      const response = await send("GET", `/admin/stones${buildQuery(query)}`);
      return parseWith(adminStoneListResponseSchema, assertOk(response), "商品列表");
    },

    async getStone(id) {
      const response = await send("GET", `/admin/stones/${id}`);
      return parseWith(adminStoneSchema, assertOk(response), "商品详情");
    },

    async createStone(payload) {
      const response = await send("POST", "/admin/stones", { body: payload });
      return parseWith(adminStoneSchema, assertOk(response), "新建结果");
    },

    async updateStone(id, patch) {
      const response = await send("PATCH", `/admin/stones/${id}`, { body: patch });
      return parseWith(adminStoneSchema, assertOk(response), "保存结果");
    },

    async addSpec(stoneId, payload) {
      const response = await send("POST", `/admin/stones/${stoneId}/specs`, { body: payload });
      return parseWith(adminStoneSchema, assertOk(response), "新增规格结果");
    },

    async updateSpec(specId, patch) {
      const response = await send("PATCH", `/admin/specs/${specId}`, { body: patch });
      return parseWith(adminStoneSchema, assertOk(response), "保存规格结果");
    },

    async uploadPhoto(stoneId, file) {
      const form = new FormData();
      form.append("file", file);
      const response = await send("POST", `/admin/stones/${stoneId}/photo`, { form });
      return parseWith(adminStoneSchema, assertOk(response), "照片上传结果");
    },

    async listCategories() {
      const response = await send("GET", "/admin/categories");
      return parseWith(adminCategoryListResponseSchema, assertOk(response), "分类列表");
    },

    async createCategory(payload) {
      const response = await send("POST", "/admin/categories", { body: payload });
      return parseWith(adminCategoryListResponseSchema, assertOk(response), "分类列表");
    },

    async uploadMedia(file) {
      const form = new FormData();
      form.append("file", file);
      const response = await send("POST", "/admin/media", { form });
      return parseWith(uploadMediaResponseSchema, assertOk(response), "上传结果");
    },

    async listWorks(query) {
      const response = await send("GET", `/admin/works${buildWorkQuery(query)}`);
      return parseWith(adminWorkListResponseSchema, assertOk(response), "作品列表");
    },

    async getWork(id) {
      const response = await send("GET", `/admin/works/${id}`);
      return parseWith(adminWorkSchema, assertOk(response), "作品详情");
    },

    async createWork(payload) {
      const response = await send("POST", "/admin/works", { body: payload });
      return parseWith(adminWorkSchema, assertOk(response), "新建作品结果");
    },

    async updateWork(id, patch) {
      const response = await send("PATCH", `/admin/works/${id}`, { body: patch });
      return parseWith(adminWorkSchema, assertOk(response), "保存作品结果");
    },

    async listDesigners() {
      const response = await send("GET", "/admin/designers");
      return parseWith(designerListResponseSchema, assertOk(response), "设计师列表");
    },

    async createDesigner(payload) {
      const response = await send("POST", "/admin/designers", { body: payload });
      return parseWith(designerListResponseSchema, assertOk(response), "设计师列表");
    },

    async updateDesigner(id, patch) {
      const response = await send("PATCH", `/admin/designers/${id}`, { body: patch });
      return parseWith(designerListResponseSchema, assertOk(response), "设计师列表");
    },

    async getHomeContent() {
      const response = await send("GET", "/admin/home-content");
      return parseWith(homeContentSchema, assertOk(response), "首页内容");
    },

    async updateHomeContent(payload) {
      const response = await send("PUT", "/admin/home-content", { body: payload });
      return parseWith(homeContentSchema, assertOk(response), "首页内容");
    },

    async listOrders(query) {
      const response = await send("GET", `/admin/orders${buildOrderQuery(query)}`);
      return parseWith(adminOrderListResponseSchema, assertOk(response), "订单列表");
    },

    async getOrder(id) {
      const response = await send("GET", `/admin/orders/${id}`);
      return parseWith(orderSchema, assertOk(response), "订单详情");
    },

    async cancelOrder(id) {
      const response = await send("POST", `/admin/orders/${id}/cancel`);
      return parseWith(orderSchema, assertOk(response), "取消订单结果");
    },

    async updateFulfillment(id, input) {
      const response = await send("POST", `/admin/orders/${id}/fulfillment`, { body: input });
      return parseWith(orderSchema, assertOk(response), "履约更新结果");
    },

    async submitOrderProof(id, input) {
      const response = await send("POST", `/admin/orders/${id}/proof`, { body: input });
      return parseWith(orderSchema, assertOk(response), "实物照片提交结果");
    },

    async reviewRefund(id, input) {
      const response = await send("POST", `/admin/orders/${id}/refund/review`, { body: input });
      return parseWith(orderSchema, assertOk(response), "退款审核结果");
    },
  };
}
